import { useQueryClient, useInfiniteQuery, UseInfiniteQueryOptions } from '@lumapps/base-api/react-query';
import { ONE_MINUTE } from '@lumapps/utils/time/constants';

import { instanceQueryKeys, listAdministrableInstances } from '../api';
import type { QueryListAdministrableInstancesOptions } from '../api/types';
import { MINIMAL_INSTANCE_FIELDS_ARRAY } from '../constants';

/**
 * List administrable instances with react-query infinite scroll.
 * Only returns sites the current user has permission to administer.
 */
export function useQueryAdministrableInstances(
    params?: QueryListAdministrableInstancesOptions,
    options?: Omit<UseInfiniteQueryOptions, 'queryKey' | 'queryFn' | 'getNextPageParam'>,
) {
    const baseParams: QueryListAdministrableInstancesOptions = {
        itemFields: MINIMAL_INSTANCE_FIELDS_ARRAY,
        ...params,
    };
    const queryClient = useQueryClient();
    return useInfiniteQuery({
        ...options,
        queryKey: instanceQueryKeys.listAdministrable(baseParams),
        queryFn: async ({ pageParam, signal }) => {
            const result = await listAdministrableInstances({ ...baseParams, cursor: pageParam }, signal);
            for (const item of result.items) {
                queryClient.setQueryData(
                    instanceQueryKeys.get(item.id, baseParams.itemFields ?? MINIMAL_INSTANCE_FIELDS_ARRAY),
                    item,
                );
            }
            return result;
        },
        getNextPageParam: (data) => (data.more && data.cursor) || undefined,
        staleTime: 5 * ONE_MINUTE,
    });
}
